'use client';

import { Fragment, useState } from 'react';
import type { ComponentType } from 'react';
import {
  Baby,
  Calendar,
  Check,
  Church,
  Clock,
  Compass,
  Gift,
  Handshake,
  HeartHandshake,
  Heart,
  HelpCircle,
  Mail,
  MapPin,
  MessageCircle,
  Minus,
  Music,
  Plus,
  Sparkles,
  User,
  Users,
} from 'lucide-react';
import type { FormAnswers, FormCampusOption, WebFormField } from './types';
import { isFollowUpChild, offeredOptions, personalise, plain, same } from './logic';
import type { AboutGender } from './logic';
import { CampusSelect } from './CampusSelect';

/**
 * One question. Branches on our `component` first, MP's type second, so a
 * field nobody configured renders the way MP's own widget would, and a field
 * with configuration renders the way we meant.
 *
 * Styling leans on the `.field` and `.chip` classes in forms.css and on the
 * colour tokens it declares (`text-navy`, `bg-teal`…). A widget imports that
 * stylesheet into its globals.css once and every form in it looks the same.
 *
 * Woodside's legacy multiselect (an Instructions parent with checkbox
 * children, one answer row per checked child) renders as a chip group. The
 * row shape is kept on purpose: staff pivots and past answers depend on it.
 */

const ICONS: Record<string, ComponentType<{ className?: string; strokeWidth?: number }>> = {
  calendar: Calendar,
  clock: Clock,
  users: Users,
  user: User,
  baby: Baby,
  heart: Heart,
  'map-pin': MapPin,
  help: HelpCircle,
  church: Church,
  compass: Compass,
  gift: Gift,
  handshake: Handshake,
  care: HeartHandshake,
  mail: Mail,
  message: MessageCircle,
  music: Music,
  sparkles: Sparkles,
};

export interface FormFieldProps {
  field: WebFormField;
  /** The field's own children (a multiselect's choices, a group's follow-up box). */
  children: WebFormField[];
  answers: FormAnswers;
  setAnswer: (id: number, value: string) => void;
  /** Campuses the CampusPicker can draw; without them a campus field is plain chips. */
  campuses?: FormCampusOption[];
  /** Treat this field as a campus question even without configuration. */
  asCampus?: boolean;
  /** Required and still unanswered, once somebody has tried to send. */
  invalid?: boolean;
  /**
   * Whose answers these are, when they are not the reader's own: a first name.
   * Null or absent means the reader is answering for themselves and the
   * question stays in the second person.
   *
   * A parent filling in four children reads "What grade are you in?" four
   * times, each about a different person, with only the heading to say which.
   * Where a field carries a `title_about`, this is what fills it.
   */
  aboutName?: string | null;
  /** What MP holds, so a pronoun can fit. Absent or unknown means they/them. */
  aboutGender?: AboutGender;
  /**
   * The gender of the person these answers are about, INCLUDING the reader
   * answering for themselves. Unlike `aboutGender` this is not about
   * wording: it is what decides whether somebody who baptizes only women can
   * be chosen. Null or absent means we do not know.
   */
  personGender?: 'male' | 'female' | null;
  /**
   * Told when the form had to ask. The host keeps it with the rest of this
   * person's answers and the server writes it to their record if MP had
   * none. Without it the answer lives only as long as the question.
   */
  onPersonGender?: (gender: 'male' | 'female') => void;
}

/** The icon for one option: its own first, then the field's. */
function iconFor(
  field: WebFormField,
  option: string | null,
  fallback?: string | null
): string | null {
  if (option && field.optionIcons) {
    const hit = Object.keys(field.optionIcons).find((k) => same(k, plain(option)));
    // The key came out of the same object, so the lookup cannot miss.
    if (hit) return field.optionIcons[hit] ?? null;
  }
  return field.icon ?? fallback ?? null;
}

function Head({
  field,
  invalid,
  aboutName,
  aboutGender,
}: {
  field: WebFormField;
  invalid?: boolean;
  aboutName?: string | null;
  aboutGender?: AboutGender;
}) {
  /*
   * The question about somebody else, where the field wrote one. Both
   * sentences are staff's: going from "do you" to "does Sarah" changes the
   * verb, and guessing at that is doing grammar on somebody else's writing.
   * Without a name or without the second sentence, the question is unchanged.
   *
   * Pronouns fill themselves in — see `personalise`. They/them where MP holds
   * no gender, which is one active contact in five.
   */
  const about = aboutName?.trim();
  const asked =
    about && field.titleAbout ? personalise(field.titleAbout, about, aboutGender) : null;
  const label = asked ?? field.title ?? field.label;
  return (
    <div className="mb-2">
      <div className="text-navy text-[0.92rem] font-bold">
        {asked || field.title || !field.labelHtml ? (
          label
        ) : (
          <span
            className="[&_a]:text-teal-deep [&_a]:underline [&_p]:inline [&_strong]:font-extrabold"
            dangerouslySetInnerHTML={{ __html: field.labelHtml }}
          />
        )}
        {field.required && <span className="text-rose ml-1">*</span>}
      </div>
      {field.helperText && <div className="text-slate text-xs">{field.helperText}</div>}
      {field.optionsNote && <div className="text-amber text-xs">{field.optionsNote}</div>}
      {invalid && (
        <div className="text-rose mt-1 text-xs font-semibold">
          {field.kind === 'instructions' ? 'Pick at least one.' : 'This one is needed.'}
        </div>
      )}
    </div>
  );
}

function Chip({
  on,
  icon,
  label,
  onClick,
  wide,
}: {
  on: boolean;
  icon?: string | null;
  label: string;
  onClick: () => void;
  wide?: boolean;
}) {
  const Icon = icon ? ICONS[icon] : undefined;
  return (
    <button
      type="button"
      className={`chip ${wide ? 'w-full justify-start !py-3 text-left !whitespace-normal' : '!py-2.5 text-left !whitespace-normal'}`}
      data-on={String(on)}
      aria-pressed={on}
      onClick={onClick}
    >
      {on ? (
        <Check className="h-3.5 w-3.5 shrink-0" strokeWidth={3} />
      ) : Icon ? (
        <Icon className="h-3.5 w-3.5 shrink-0 opacity-70" strokeWidth={2.25} />
      ) : null}
      {label}
    </button>
  );
}

const CHIP_SELECT_MAX = 6;
/**
 * An option long enough to be a sentence reads as a card, not a chip: the
 * Connect Card's spiritual-journey question and Share Your Story's consent
 * are four and three sentences respectively, and as chips they wrap into an
 * unreadable wall. Short lists only — a long list of long options is a
 * dropdown's job.
 */
const CARD_OPTION_CHARS = 40;
/**
 * A BAND'S NAME, IN ART WHERE THERE IS ART.
 *
 * The mark replaces the words when it loads, and the words come back if it
 * does not. That fallback is the whole point: the first version hid the label
 * whenever a logo was configured, so a mark that 404'd left a band of options
 * with nothing naming it — which is exactly what happened when the art was
 * still an unresolved file name.
 *
 * The label is in the DOM either way. A legend a screen reader cannot read is
 * not a legend.
 */
function BandLegend({ label, logo, id }: { label: string; logo: string | null; id: string }) {
  const [broken, setBroken] = useState(false);
  const showArt = Boolean(logo) && !broken;
  return (
    <span id={id} className="flex shrink-0 items-center">
      {logo && !broken ? (
        <img
          src={logo}
          alt=""
          aria-hidden
          onError={() => setBroken(true)}
          className="h-7 w-auto max-w-[5rem] object-contain"
        />
      ) : null}
      <span
        className={
          showArt ? 'sr-only' : 'text-xs font-bold tracking-widest text-gray-500 uppercase'
        }
      >
        {label}
      </span>
    </span>
  );
}

/**
 * A SCALE, WHERE THE OPTIONS ARE A PROGRESSION.
 *
 * "This is my first time" to "I am here most weeks" is an ordinal run, and
 * three stacked cards make it look like three unrelated choices. A track with
 * stops says the thing the content already is.
 *
 * IT IS NOT A SLIDER, AND THAT IS THE POINT. An <input type="range"> always
 * has a value: the thumb sits somewhere from the first paint, so a student who
 * skips the question is recorded as whatever the thumb was resting on. This
 * field is optional and exists so leaders know who to look for, which makes a
 * manufactured "first time" worse than a blank. Radio semantics keep unanswered
 * a real state, and pressing the chosen stop again returns to it.
 *
 * The labels are the SHORT forms from the field's config; the stored answer is
 * still the full sentence, because that is what a roster is read off.
 *
 * Stops are laid out in equal columns with the dot centred, so the track runs
 * from the first dot's centre to the last one's and the ends have room for
 * their own labels.
 */
/**
 * A face on a chip, with initials when there is no photo or it will not load.
 *
 * Same fallback the band logos use: art that fails must leave a name behind,
 * never a broken frame.
 */
function PersonFace({ name, image }: { name: string; image: string | null }) {
  const [broken, setBroken] = useState(false);
  const initials = name
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();
  if (!image || broken) {
    return (
      <span className="bg-mist text-slate-deep flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[0.62rem] font-bold">
        {initials}
      </span>
    );
  }
  return (
    <img
      src={image}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className="h-7 w-7 shrink-0 rounded-full object-cover"
    />
  );
}

/**
 * A QUESTION ABOUT A PERSON, ANSWERED BY PICKING ONE.
 *
 * "Who would you like to be baptized by?" was a text box, which is how one
 * pastor's name arrives spelled four ways and how somebody who does not
 * know the staff answers "idk". Faces fix both: the people who actually do
 * this are on the screen, and choosing is recognising rather than recalling.
 *
 * THE ANSWER IS STILL THE NAME. MP gets the same string it always got, so
 * every report, export and summary reads exactly as before and nobody has
 * to learn what a contact id is.
 *
 * AND SOMEBODY ELSE IS ALWAYS POSSIBLE. A named list that cannot be escaped
 * is a worse question than the text box was — the answer might be a
 * grandfather, a former pastor, or somebody nobody thought of. The escape
 * hatch is a chip like the others and opens the box that used to be the
 * whole question.
 *
 * SOMEBODY WHO DOES THIS FOR ONE GENDER STAYS ON SCREEN FOR EVERYONE. A man
 * sees Tiffani faded rather than missing: hiding her would make the list
 * look different depending on who is asking, and the faded pill is the
 * honest answer to "can she baptize me?". Where MP does not know the
 * person's gender, tapping her asks once, right there, and the answer
 * decides it; the host sends it on so the record learns it too.
 */
function PeoplePicker({
  field,
  value,
  onChange,
  ring,
  invalid,
  aboutName,
  personGender,
  onPersonGender,
}: {
  field: WebFormField;
  value: string;
  onChange: (v: string) => void;
  ring: string;
  invalid?: boolean;
  aboutName?: string | null;
  personGender?: 'male' | 'female' | null;
  onPersonGender?: (gender: 'male' | 'female') => void;
}) {
  const named = field.people ?? [];
  const other = field.otherLabel ?? 'Someone else';
  // What they told us here, for a host that is not keeping it. A host that
  // is keeping it passes it straight back as `personGender`.
  const [told, setTold] = useState<'male' | 'female' | null>(null);
  const gender = personGender ?? told;
  // The restricted person somebody tapped while we did not know, waiting on
  // the answer to "male or female?".
  const [asking, setAsking] = useState<string | null>(null);
  // A faded pill somebody tapped anyway. It stays tappable so the tap can
  // say why; a disabled button would swallow it and look broken.
  const [barredTap, setBarredTap] = useState<{ name: string; only: 'male' | 'female' } | null>(
    null
  );

  const isNamed = named.some((p) => p.name === value);
  // Typed something that is not a name on the list, or tapped the hatch and
  // has not typed yet — either way the box is theirs and stays open.
  const onOther = value.length > 0 && !isNamed;

  const answerGender = (g: 'male' | 'female') => {
    setTold(g);
    onPersonGender?.(g);
    const waiting = named.find((p) => p.name === asking);
    setAsking(null);
    if (!waiting?.only) return;
    // The person they tapped either becomes the answer or says why not,
    // rather than quietly fading out under them.
    if (waiting.only === g) onChange(waiting.name);
    else setBarredTap({ name: waiting.name, only: waiting.only });
  };

  return (
    <>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {named.map((p) => {
          const on = p.name === value;
          const barred = Boolean(p.only && gender && p.only !== gender);
          return (
            <button
              key={p.name}
              type="button"
              aria-pressed={on}
              aria-disabled={barred || undefined}
              onClick={() => {
                if (barred && p.only) return setBarredTap({ name: p.name, only: p.only });
                setBarredTap(null);
                if (on) return onChange('');
                if (p.only && !gender) return setAsking(p.name);
                setAsking(null);
                onChange(p.name);
              }}
              data-on={on}
              className="chip !py-1 !pr-3.5 !pl-1 aria-disabled:cursor-not-allowed aria-disabled:opacity-45"
            >
              <PersonFace name={p.name} image={p.image} />
              {p.name}
            </button>
          );
        })}
        <button
          type="button"
          aria-pressed={onOther}
          onClick={() => {
            setAsking(null);
            setBarredTap(null);
            onChange(onOther ? '' : ' ');
          }}
          data-on={onOther}
          className="chip"
        >
          {other}
        </button>
      </div>
      <p role="status" className="text-slate-deep mt-2 text-sm empty:hidden">
        {barredTap
          ? `${barredTap.name.split(/\s+/)[0]} is available for ${barredTap.only === 'female' ? 'women' : 'men'} only.`
          : ''}
      </p>
      {asking && (
        <div className="mt-3" role="group" aria-label="Gender">
          <p className="text-navy text-sm font-semibold">
            {aboutName ? `Is ${aboutName} male or female?` : 'Are you male or female?'}
          </p>
          <div className="mt-1.5 flex flex-wrap gap-2">
            {(['female', 'male'] as const).map((g) => (
              <button key={g} type="button" className="chip" onClick={() => answerGender(g)}>
                {g === 'female' ? 'Female' : 'Male'}
              </button>
            ))}
          </div>
        </div>
      )}
      {onOther && (
        <input
          className={`field mt-2 ${ring}`}
          type="text"
          autoFocus
          value={value.trim() === '' ? '' : value}
          onChange={(e) => onChange(e.target.value)}
          maxLength={120}
          aria-label={other}
          aria-invalid={invalid || undefined}
        />
      )}
    </>
  );
}

function Scale({
  options,
  value,
  labelOf,
  onChange,
  groupLabel,
}: {
  options: string[];
  value: string;
  labelOf: (option: string) => string;
  onChange: (next: string) => void;
  groupLabel: string;
}) {
  const n = options.length;
  const at = options.findIndex((o) => same(value, o));

  /*
   * Edge to edge. The first stop sits on the left edge of the question and the
   * last on the right, so the scale lines up with the heading above it instead
   * of floating inset inside it.
   *
   * The arithmetic is in the dot's own width: the row is inset half a dot at
   * each end, and the stops divide what is left. So stop i is at
   * 7.5px + (100% - 15px) * i/(n-1), which puts the outer dots flush against
   * the edges rather than hanging over them.
   */
  const DOT = 15;
  const along = (i: number) =>
    `calc(${DOT / 2}px + (100% - ${DOT}px) * ${n > 1 ? i / (n - 1) : 0})`;

  const move = (delta: number) => {
    // From nothing, either arrow steps onto the nearest end rather than
    // jumping to the middle.
    const next = at === -1 ? (delta > 0 ? 0 : n - 1) : Math.min(n - 1, Math.max(0, at + delta));
    onChange(options[next] ?? '');
  };

  return (
    <div
      role="radiogroup"
      aria-label={groupLabel}
      className="relative pt-2 pb-1"
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
          e.preventDefault();
          move(1);
        } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
          e.preventDefault();
          move(-1);
        }
      }}
    >
      {/*
        The track and the dots, drawn over the buttons and deaf to the mouse,
        so the whole band under a stop stays the tap target rather than a
        15-pixel circle.
      */}
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-2 h-[15px]">
        <span
          className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full bg-gray-200"
          style={{ left: `${DOT / 2}px`, right: `${DOT / 2}px` }}
        />
        {at > 0 && (
          <span
            className="absolute top-1/2 h-[3px] -translate-y-1/2 rounded-full transition-all duration-300"
            style={{
              left: `${DOT / 2}px`,
              width: `calc((100% - ${DOT}px) * ${at / (n - 1)})`,
              /* The event's colour where it has one: the widget redefines
                 --color-teal per event, so this follows the artwork and falls
                 back to the church's teal everywhere else. */
              background: 'var(--color-teal)',
            }}
          />
        )}
        {options.map((o, i) => {
          const on = i === at;
          const passed = at > -1 && i <= at;
          return (
            <span
              key={o}
              className={`absolute top-0 h-[15px] w-[15px] -translate-x-1/2 rounded-full border-2 transition-all ${
                on ? 'scale-125' : ''
              }`}
              style={{
                left: along(i),
                borderColor: passed ? 'var(--color-teal)' : 'var(--color-slate-light, #d1d5db)',
                background: passed ? 'var(--color-teal)' : '#fff',
              }}
            />
          );
        })}
      </div>

      <div className="flex">
        {options.map((o, i) => {
          const on = i === at;
          return (
            <button
              key={o}
              type="button"
              role="radio"
              aria-checked={on}
              tabIndex={on || (at === -1 && i === 0) ? 0 : -1}
              onClick={() => onChange(on ? '' : o)}
              className={`flex flex-1 cursor-pointer flex-col bg-transparent ${
                i === 0 ? 'items-start' : i === n - 1 ? 'items-end' : 'items-center'
              }`}
            >
              <span className="block h-[15px]" />
              <span
                className={`mt-2 text-xs leading-tight ${
                  i === 0 ? 'text-left' : i === n - 1 ? 'text-right' : 'text-center'
                } ${on ? 'font-bold' : 'font-semibold text-gray-500'}`}
                style={on ? { color: 'var(--color-navy)' } : undefined}
              >
                {labelOf(o)}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const CARD_OPTION_MAX = 5;

export function FormField({
  field,
  children,
  answers,
  setAnswer,
  campuses,
  asCampus,
  invalid,
  aboutName,
  aboutGender,
  personGender,
  onPersonGender,
}: FormFieldProps) {
  const value = answers[String(field.id)] ?? '';
  const onChange = (v: string) => setAnswer(field.id, v);
  const options = offeredOptions(field);
  const component = field.component;
  const ring = invalid ? '!border-rose ring-2 ring-rose/20' : '';

  if (field.kind === 'instructions' && (field.isMultiselect || children.length > 0)) {
    const choices = children.filter((c) => !isFollowUpChild(c));
    const followUps = children.filter(isFollowUpChild);
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <div className="flex flex-wrap gap-2">
          {choices.map((c) => {
            const on = same(answers[String(c.id)], 'Yes');
            return (
              <Chip
                key={c.id}
                on={on}
                icon={iconFor(field, c.title ?? c.label, c.icon)}
                label={c.title ?? c.label}
                onClick={() => setAnswer(c.id, on ? '' : 'Yes')}
              />
            );
          })}
        </div>
        {followUps.map((c) => (
          <div key={c.id} className="mt-3">
            <FormField
              field={c}
              children={[]}
              answers={answers}
              setAnswer={setAnswer}
              aboutName={aboutName}
              aboutGender={aboutGender}
            />
          </div>
        ))}
      </div>
    );
  }

  if (field.kind === 'instructions') {
    return (
      <div
        className="bg-mist text-slate-deep mp-html rounded-2xl px-4 py-3 text-sm"
        dangerouslySetInnerHTML={{ __html: field.labelHtml ?? field.label }}
      />
    );
  }

  if (field.kind === 'file') {
    return (
      <div>
        <Head field={field} aboutName={aboutName} aboutGender={aboutGender} />
        <p className="border-line text-slate rounded-2xl border border-dashed px-4 py-3 text-xs">
          File uploads are not available here yet. The team will follow up if they need this.
        </p>
      </div>
    );
  }

  if (asCampus || component === 'CampusPicker') {
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <CampusSelect
          field={field}
          value={value}
          onChange={onChange}
          campuses={campuses ?? []}
          invalid={invalid}
        />
      </div>
    );
  }

  if (
    component === 'YesNo' ||
    (field.kind === 'radio' && options.length === 2 && /^(yes|no)$/i.test(plain(options[0])))
  ) {
    const pair = options.length === 2 ? options : ['Yes', 'No'];
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <div className="flex gap-2">
          {pair.map((o) => (
            <button
              key={o}
              type="button"
              className="chip !px-5 !py-2.5"
              data-on={String(same(value, o))}
              aria-pressed={same(value, o)}
              onClick={() => onChange(same(value, o) ? '' : o)}
            >
              {plain(o)}
            </button>
          ))}
        </div>
      </div>
    );
  }

  if (component === 'Counter') {
    const n = Number(value) || 0;
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <div className="border-line bg-paper inline-flex items-center gap-1 rounded-full border p-1">
          <button
            type="button"
            className="hover:bg-mist flex h-8 w-8 items-center justify-center rounded-full"
            onClick={() => onChange(String(Math.max(0, n - 1)))}
            aria-label="Fewer"
          >
            <Minus className="h-4 w-4" />
          </button>
          <span className="w-8 text-center font-bold">{n}</span>
          <button
            type="button"
            className="hover:bg-mist flex h-8 w-8 items-center justify-center rounded-full"
            onClick={() => onChange(String(n + 1))}
            aria-label="More"
          >
            <Plus className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  }

  if (field.kind === 'checkbox') {
    const on = same(value, 'Yes');
    const toggle = () => onChange(on ? '' : 'Yes');
    return (
      <div className="flex cursor-pointer items-start gap-3" onClick={toggle}>
        <span
          className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${on ? 'border-teal bg-teal text-white' : `border-line bg-paper ${ring}`}`}
          role="checkbox"
          aria-checked={on}
          tabIndex={0}
          onKeyDown={(e) => (e.key === ' ' || e.key === 'Enter') && toggle()}
        >
          {on && <Check className="h-3.5 w-3.5" strokeWidth={3} />}
        </span>
        <span className="text-navy text-[0.92rem] font-semibold">
          {field.title ?? field.label}
          {field.required && field.parentId === null && <span className="text-rose ml-1">*</span>}
          {field.helperText && (
            <span className="text-slate block text-xs font-normal">{field.helperText}</span>
          )}
        </span>
      </div>
    );
  }

  /** The short form where the field gave one; the stored answer is unchanged. */
  const labelOf = (o: string) => field.optionLabels?.[o] ?? plain(o);

  /*
   * A scale only where the options are a run of two or more. One stop is not a
   * scale, and a field configured for one with nothing to put on it falls
   * through to the ordinary controls rather than drawing an empty track.
   */
  if (component === 'Scale' && options.length > 1) {
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <Scale
          options={options}
          value={value}
          labelOf={labelOf}
          onChange={onChange}
          groupLabel={field.label}
        />
      </div>
    );
  }

  const asCards =
    component === 'Cards' ||
    (options.length <= CARD_OPTION_MAX &&
      options.length > 1 &&
      options.some((o) => plain(o).length > CARD_OPTION_CHARS));
  if (
    asCards ||
    component === 'Pills' ||
    component === 'Radio' ||
    field.kind === 'radio' ||
    (field.kind === 'select' && options.length <= CHIP_SELECT_MAX)
  ) {
    const chip = (o: string) => (
      <Chip
        key={o}
        on={same(value, o)}
        icon={iconFor(field, o)}
        // `labelOf`, not `plain`: a field that gave its options short forms
        // meant them here too. Until now `option_labels` reached the Scale and
        // nothing else, so "8:30 AM · 1st Service" was configured, sent to the
        // browser, and quietly ignored by the chips that were meant to show it.
        label={labelOf(o)}
        wide={asCards}
        onClick={() => onChange(same(value, o) ? '' : o)}
      />
    );
    const row = asCards ? 'flex flex-col gap-2' : 'flex flex-wrap gap-2';

    /*
     * BANDS, WHERE THE CHURCH SAYS THERE ARE BANDS.
     *
     * Seven grades are seven circles until somebody tells you 6-8 is Oasis and
     * 9-12 is Wake, and then they are two questions with an obvious answer.
     * The split is configuration, not arithmetic: nothing here knows what a
     * grade is, and a middle-school-only event names one band.
     *
     * A real fieldset and legend, because the grouping is structure rather
     * than decoration — a screen reader should say "Middle School, 6".
     *
     * Anything the bands did not claim renders after them, unbanded. A grade
     * added in MP must never disappear off the form because the configuration
     * was not updated with it.
     */
    const bands = field.optionGroups ?? [];
    if (bands.length > 0) {
      const claimed = new Set(bands.flatMap((b) => b.options.map((o) => plain(o).toLowerCase())));
      const rest = options.filter((o) => !claimed.has(plain(o).toLowerCase()));
      return (
        <div>
          <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
          {/*
            The mark sits IN the row it labels, not above it. Stacked, a badge
            on a line of its own reads as an orphaned bullet and costs two rows
            of height for no information. Beside its own options it reads as
            what it is: these grades are Oasis, those are Wake.

            A hairline between bands does the separating, so nothing is said
            twice. Each band keeps its options together and the line breaks
            BETWEEN bands, because a band split across two lines is the one
            arrangement that reads worse than stacking.
          */}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-3">
            {bands.map((band, i) => {
              const mine = options.filter((o) => band.options.some((b) => same(b, o)));
              if (mine.length === 0) return null;
              const labelId = `band-${field.id}-${i}`;
              return (
                <Fragment key={band.label}>
                  {i > 0 && <span aria-hidden className="h-7 w-px shrink-0 bg-gray-200" />}
                  <div
                    role="group"
                    aria-labelledby={labelId}
                    className="flex flex-wrap items-center gap-2"
                  >
                    <BandLegend label={band.label} logo={band.logo} id={labelId} />
                    {mine.map(chip)}
                  </div>
                </Fragment>
              );
            })}
            {rest.length > 0 && rest.map(chip)}
          </div>
        </div>
      );
    }

    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <div className={row}>{options.map(chip)}</div>
      </div>
    );
  }

  if (field.kind === 'select') {
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <select
          className={`field ${ring}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={invalid || undefined}
        >
          <option value="">Choose…</option>
          {options.map((o) => (
            <option key={o} value={o}>
              {plain(o)}
            </option>
          ))}
        </select>
      </div>
    );
  }

  if (field.people?.length) {
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <PeoplePicker
          field={field}
          value={value}
          onChange={onChange}
          ring={ring}
          invalid={invalid}
          aboutName={aboutName}
          personGender={personGender}
          onPersonGender={onPersonGender}
        />
      </div>
    );
  }

  if (field.kind === 'memo' || component === 'LongText') {
    /**
     * A LENGTH THE QUESTION ACTUALLY MEANT.
     *
     * "In 3 to 4 brief sentences" is a request the box could not keep: it
     * held four thousand characters and said nothing. Where a field names a
     * limit the box keeps it AND shows it, because a limit somebody only
     * discovers by being cut off mid-word is worse than no limit at all.
     *
     * The count appears once there is something to count, so an empty form is
     * not a wall of zeroes, and it turns amber for the last tenth — far
     * enough ahead to finish a sentence rather than lose one.
     */
    const cap = field.maxLength ?? null;
    const used = value.length;
    const near = cap != null && used >= cap * 0.9;
    return (
      <div>
        <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
        <textarea
          className={`field min-h-28 ${ring}`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={field.placeholder ?? undefined}
          maxLength={cap ?? 4000}
          aria-invalid={invalid || undefined}
        />
        {cap != null && used > 0 && (
          <p
            className={`mt-1 text-right text-xs tabular-nums ${near ? 'text-amber' : 'text-slate'}`}
            aria-live="polite"
          >
            {used} / {cap}
          </p>
        )}
      </div>
    );
  }

  const type = field.kind === 'date' ? 'date' : field.kind === 'phone' ? 'tel' : 'text';
  return (
    <div>
      <Head field={field} invalid={invalid} aboutName={aboutName} aboutGender={aboutGender} />
      <input
        className={`field ${ring}`}
        type={type}
        value={value}
        onChange={(e) =>
          onChange(field.kind === 'phone' ? formatPhone(e.target.value) : e.target.value)
        }
        maxLength={type === 'date' ? undefined : 500}
        placeholder={field.placeholder ?? undefined}
        autoComplete={type === 'tel' ? 'tel' : 'off'}
        aria-invalid={invalid || undefined}
      />
    </div>
  );
}

/**
 * A phone number as it is typed: ten digits become "(248) 555-0134" on the way
 * in, so what MP stores looks the same whoever filled the form in. A leading +
 * or more than ten digits is left exactly as entered — an international number
 * or an extension is not ours to reshape.
 *
 * Idempotent, which matters because callers may already have formatted the
 * value they seeded the field with.
 */
function formatPhone(input: string): string {
  const digits = input.replace(/\D/g, '');
  if (input.trim().startsWith('+') || digits.length > 10) return input;
  if (digits.length < 4) return digits;
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}
